import { FC, ReactElement, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { TelerehabilitationProgramDto } from '../../../../shared/dtos/telerehabilitationProgram.dto';
import { Alert, Button, Col, Form, Row, Spinner, Stack } from 'react-bootstrap';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import i18next from 'i18next';
import ContraindicationModal from './ContraindicationModal';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { isValidSecurityNumber } from '../../util/validators/socialSecurityValidator';
import { CreatePatientRequest } from '../../../../shared/requests/createPatient.request';
import { PatientEditRequest } from '../../../../shared/requests/patientEdit.request';
import DatePickerField from '../DatePickerField';
import { toUtcDateOnlyIso } from '../../util/date-utils';

type PatientFormData = {
  patientId: string;
  firstName: string;
  lastName: string;
  email: string;
  socialSecurityNumber?: string;
  startDate?: Date;
  endDate?: Date;
  telerehabilitationProgram?: TelerehabilitationProgramDto;
}

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  telerehabilitationProgramId?: string | null;
  socialSecurityNumber?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
}

type PatientFormProps = {
  data: PatientFormData | null;
  telerehabilitationPrograms: TelerehabilitationProgramDto[];
  onClose: () => void;
  onSubmit: (savedSocialSecurityNumber?: string | null) => void;
}

const PatientForm: FC<PatientFormProps> = ({ data, telerehabilitationPrograms, onClose, onSubmit }: PatientFormProps): ReactElement => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showContraindicationModal, setShowContraindicationModal] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState<'startDate' | 'endDate' | null>(null);

  const [createPatientData, setCreatePatientData] = useState<{
    values: FormValues;
    formikHelpers: FormikHelpers<FormValues>
  } | null>(null);
  
  
  const { authFetch } = useAuthFetchFromRestAPI();
  
  const isEditMode: boolean = data !== null;
  
  const initialValues: FormValues = useMemo(() => ({
    firstName: data?.firstName ?? '',
    lastName: data?.lastName ?? '',
    email: data?.email ?? '',
    socialSecurityNumber: data?.socialSecurityNumber ?? null,
    startDate: data?.startDate ?? null,
    endDate: data?.endDate ?? null,
    telerehabilitationProgramId: data?.telerehabilitationProgram?.id ?? null,
  }), [data]);
  
  const validationSchema: Yup.ObjectSchema<FormValues> = useMemo(() => Yup.object().shape({
    firstName: Yup.string().required(i18next.t('generic.required')),
    lastName: Yup.string().required(i18next.t('generic.required')),
    email: isEditMode
      ? Yup.string().defined().ensure()
      : Yup.string().email(i18next.t('generic.invalidEmail')).required(i18next.t('generic.required')),
    socialSecurityNumber: Yup.string().nullable().notRequired().test('social-security-number', i18next.t('patientForm.invalidSsn'), (value) => {
      if (!value) {
        return true;
      }
      
      return isValidSecurityNumber(value);
    }),
    startDate: Yup.date().nullable().notRequired(),
    endDate: Yup.date().nullable().notRequired().test('end-date-after-start-date', i18next.t('patientForm.invalidDates'), function (value) {
      const { startDate } = this.parent;
      
      if (!value || !startDate) {
        return true;
      }
      
      return value >= startDate;
    }),
    telerehabilitationProgramId: isEditMode ? Yup.string().required(i18next.t('generic.required')) : Yup.string().nullable().notRequired()
  }), [isEditMode]);
  
  const createPatient = async (): Promise<void> => {
    if (!createPatientData) {
      return;
    }
    
    const { values, formikHelpers } = createPatientData;
    const { setSubmitting } = formikHelpers;
    
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);
    
    try {
      const request: CreatePatientRequest = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        socialSecurityNumber: values.socialSecurityNumber || null,
        teleRehaAftercareStart: toUtcDateOnlyIso(values.startDate),
        teleRehaAftercareEnd: toUtcDateOnlyIso(values.endDate),
        telerehabilitationProgramId: values.telerehabilitationProgramId,
      };
      
      await authFetch('/api/v1/patients/create', {
        method: 'POST',
        body: request
      });
      
      onSubmit();
    } catch (error: unknown) {
      let message: string;
      
      if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === 'string') {
        message = error;
      } else {
        message = i18next.t('generic.unknownError');
      }
      setErrorMessage(message);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  }
  
  const editPatient = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    if (!data) {
      return;
    }
    
    setErrorMessage(null);
    setIsSaving(true);
    setSubmitting(true);
    
    try {
      const request: PatientEditRequest = {
        firstName: values.firstName,
        lastName: values.lastName,
        socialSecurityNumber: values.socialSecurityNumber || null,
        teleRehaAftercareStart: toUtcDateOnlyIso(values.startDate),
        teleRehaAftercareEnd: toUtcDateOnlyIso(values.endDate),
        telerehabilitationProgramId: values.telerehabilitationProgramId,
      };
      
      await authFetch(`/api/v1/patients/edit/${data.patientId}`, {
        method: 'PATCH',
        body: request
      });

      onSubmit(request.socialSecurityNumber ?? null);
    } catch (error: unknown) {
      let message: string;

      if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === 'string') {
        message = error;
      } else {
        message = i18next.t('generic.unknownError');
      }
      setErrorMessage(message);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  }
  
  const handleSubmit = (values: FormValues, formikHelpers: FormikHelpers<FormValues>): void => {
    if (isEditMode) {
      void editPatient(values, formikHelpers);
      return;
    }
    
    setCreatePatientData({ values, formikHelpers });
    setShowContraindicationModal(true);
  }
  
  return (
    <>
      <div className="position-relative">
        {isSaving && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
            <Spinner animation="border" role="status" />
          </div>
        )}

        {errorMessage && (
          <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
        )}

        <Formik<FormValues>
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}>
          {({ values, setFieldValue, touched, errors, handleSubmit, handleChange, handleBlur, isSubmitting }: FormikProps<FormValues>) => (
            <Form
              noValidate
              onSubmit={handleSubmit}
              className="p-2"
              style={{ opacity: isSaving ? 0.5 : 1 }}>
              <Stack gap={3}>
                <Row>
                  <Col>
                    <Form.Group controlId="firstName">
                      <Form.Label>
                        {i18next.t('patientForm.firstName')}
                      </Form.Label>
                      <Form.Control
                        name="firstName"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.firstName && !!errors.firstName}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors.firstName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  
                  <Col>
                    <Form.Group controlId="lastName">
                      <Form.Label>
                        {i18next.t('patientForm.lastName')}
                      </Form.Label>
                      <Form.Control
                        name="lastName"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.lastName && !!errors.lastName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.lastName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col>
                    <Form.Group controlId="socialSecurityNumber">
                      <Form.Label>
                        {i18next.t('patientForm.socialSecurityNumber')}
                      </Form.Label>
                      <Form.Control
                        name="socialSecurityNumber"
                        value={values.socialSecurityNumber ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.socialSecurityNumber && !!errors.socialSecurityNumber}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.socialSecurityNumber}
                      </Form.Control.Feedback>

                    </Form.Group>
                  </Col>
                  
                  <Col>
                    <Form.Group controlId="email">
                      <Form.Label>
                        {i18next.t('patientForm.email')}
                      </Form.Label>
                      <Form.Control
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        readOnly={isEditMode}
                        disabled={isEditMode}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col>
                    <DatePickerField 
                      name="startDate" 
                      label={i18next.t('patientForm.startDate')} 
                      value={values.startDate ?? null} 
                      touched={touched.startDate}
                      error={errors.startDate}
                      isOpen={openDatePicker === 'startDate'}
                      onToggle={() => {
                        setOpenDatePicker((current: 'startDate' | 'endDate' | null) => current === 'startDate' ? null : 'startDate')
                      }}
                      onClose={() => setOpenDatePicker(null)}
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                  </Col>

                  <Col>
                    <DatePickerField
                      name="endDate" 
                      label={i18next.t('patientForm.endDate')} 
                      value={values.endDate ?? null}
                      touched={touched.endDate}
                      error={errors.endDate}
                      isOpen={openDatePicker === 'endDate'}
                      disabledDays={values.startDate ? { before: values.startDate } : undefined}
                      onToggle={() => {
                        setOpenDatePicker((current: 'startDate' | 'endDate' | null) => current === 'endDate' ? null : 'endDate')
                      }}
                      onClose={() => setOpenDatePicker(null)}
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                  </Col>
                </Row>

                <Row>
                  <Form.Group controlId="telerehabilitationProgramId">
                    <Form.Label>
                      {i18next.t('patientForm.teleRehaProgram')}
                    </Form.Label>
                    <Form.Select
                      name="telerehabilitationProgramId"
                      className="select-xl"
                      value={values.telerehabilitationProgramId ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.telerehabilitationProgramId && !!errors.telerehabilitationProgramId}
                      disabled={telerehabilitationPrograms.length === 0}
                    >
                      <option value="">{i18next.t('patientForm.selectTeleRehaProgram')}</option>

                      {telerehabilitationPrograms.map((program: TelerehabilitationProgramDto) => (
                        <option
                          key={program.id}
                          value={program.id}>
                          {i18next.language === 'de' ? program.germanName : program.englishName}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.telerehabilitationProgramId}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting || isSaving}>
                    {i18next.t('generic.close')}
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || isSaving}>
                    {i18next.t(isEditMode ? 'generic.save': 'patientForm.create')}
                  </Button>
                </div>
              </Stack>
            </Form>
          )}
        </Formik>
      </div>

      {!isEditMode && (
        <ContraindicationModal
          show={showContraindicationModal}
          onCancel={() => setShowContraindicationModal(false)}
          onAccept={() => {
            setShowContraindicationModal(false);
            void createPatient();
          }}
        />
      )}
    </>
  );
}

export default PatientForm;